/**
 * CaseConditionsPanel — the court's conditions and their deadlines (Lloyd, Aug 21 2026).
 *
 * The structured list the closing letter is made of, and which the deferral / DSC workflows need
 * anyway. Two things the panel keeps honest:
 *
 *   · **Readiness is shown, not discovered.** The closing letter refuses to generate while any
 *     deadline is blank; this shows those blockers next to the list so staff fix them here rather
 *     than meeting a 422 on a letter they have already told a client is coming.
 *   · **A completed condition is marked, not deleted.** Delete is for a mis-entry; "done" is a date,
 *     and it is what drops the condition off the outstanding list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConditionsApi, {
  CONDITION_TYPES,
  CONDITION_TYPE_LABELS,
  CaseConditionDto,
  ClosingLetterReadiness,
  ConditionType,
} from '../../api/ConditionsApi';

interface CaseConditionsPanelProps {
  Ticket_guid: string;
}

const emptyForm = {
  type: 'DSC_COURSE' as ConditionType,
  dueAt: '',
  amount: '',
  hours: '',
  detail: '',
};

const CaseConditionsPanel: React.FC<CaseConditionsPanelProps> = ({ Ticket_guid }) => {
  const [conditions, setConditions] = useState<CaseConditionDto[]>([]);
  const [readiness, setReadiness] = useState<ClosingLetterReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyGuid, setBusyGuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ConditionsApi.list(Ticket_guid);
      setConditions(result.conditions);
      setReadiness(result.readiness);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the conditions');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await ConditionsApi.create(Ticket_guid, {
        CaseCondition_Type: form.type,
        CaseCondition_DueAt: form.dueAt || null,
        CaseCondition_AmountCents:
          form.type === 'PAYMENT' && form.amount.trim() !== ''
            ? Math.round(Number(form.amount) * 100)
            : null,
        CaseCondition_Hours:
          form.type === 'COMMUNITY_SERVICE' && form.hours.trim() !== ''
            ? Number(form.hours)
            : null,
        CaseCondition_Detail: form.detail.trim() || null,
      });
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the condition');
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (condition: CaseConditionDto) => {
    setBusyGuid(condition.CaseCondition_guid);
    setError(null);
    try {
      await ConditionsApi.update(Ticket_guid, condition.CaseCondition_guid, {
        CaseCondition_CompletedAt: condition.CaseCondition_CompletedAt
          ? null
          : new Date().toISOString().slice(0, 10),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the condition');
    } finally {
      setBusyGuid(null);
    }
  };

  const remove = async (guid: string) => {
    if (!window.confirm('Remove this condition? Use this only for a mistaken entry.')) return;
    setBusyGuid(guid);
    setError(null);
    try {
      await ConditionsApi.remove(Ticket_guid, guid);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete the condition');
    } finally {
      setBusyGuid(null);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Court conditions &amp; deadlines
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every condition the court imposed and the date it is due by. The closing letter is generated
        from these.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {readiness && !readiness.canGenerate && readiness.blockers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>The closing letter cannot be generated yet</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {readiness.blockers.map((b, i) => (
              <li key={`${b.index ?? 'case'}-${i}`}>{b.reason}</li>
            ))}
          </ul>
        </Alert>
      )}
      {readiness && readiness.canGenerate && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Every condition has a deadline — the closing letter can be generated.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : conditions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No conditions recorded yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {conditions.map((c) => {
            const done = Boolean(c.CaseCondition_CompletedAt);
            return (
              <Box
                key={c.CaseCondition_guid}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  opacity: done ? 0.6 : 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {CONDITION_TYPE_LABELS[c.CaseCondition_Type as ConditionType] ??
                      c.CaseCondition_Type}
                    {c.CaseCondition_AmountCents != null
                      ? ` — $${(c.CaseCondition_AmountCents / 100).toFixed(2)}`
                      : ''}
                    {c.CaseCondition_Hours != null ? ` — ${c.CaseCondition_Hours}h` : ''}
                  </Typography>
                  {c.CaseCondition_Detail && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {c.CaseCondition_Detail}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      color={c.CaseCondition_DueAt ? 'default' : 'warning'}
                      variant="outlined"
                      label={
                        c.CaseCondition_DueAt
                          ? `Due ${new Date(c.CaseCondition_DueAt).toLocaleDateString()}`
                          : 'No deadline set'
                      }
                    />
                    {done && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`Done ${new Date(c.CaseCondition_CompletedAt as string).toLocaleDateString()}`}
                      />
                    )}
                  </Stack>
                </Box>
                <Button size="small" disabled={busyGuid === c.CaseCondition_guid} onClick={() => toggleDone(c)}>
                  {done ? 'Reopen' : 'Mark done'}
                </Button>
                <IconButton
                  size="small"
                  disabled={busyGuid === c.CaseCondition_guid}
                  onClick={() => remove(c.CaseCondition_guid)}
                  aria-label="remove condition"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add a condition
      </Typography>
      <Stack spacing={2} sx={{ maxWidth: 480 }}>
        <TextField
          select
          label="Type"
          size="small"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value as ConditionType })}
        >
          {CONDITION_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {CONDITION_TYPE_LABELS[t]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Due date"
          type="date"
          size="small"
          value={form.dueAt}
          onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
          InputLabelProps={{ shrink: true }}
          helperText="Can be left blank now, but the closing letter needs it"
        />
        {form.type === 'PAYMENT' && (
          <TextField
            label="Amount (USD)"
            type="number"
            size="small"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
          />
        )}
        {form.type === 'COMMUNITY_SERVICE' && (
          <TextField
            label="Hours"
            type="number"
            size="small"
            value={form.hours}
            onChange={(e) => setForm({ ...form, hours: e.target.value })}
            inputProps={{ min: 0, step: 1 }}
          />
        )}
        <TextField
          label={form.type === 'OTHER' ? "The court's wording (required)" : 'Detail (optional)'}
          size="small"
          multiline
          minRows={2}
          value={form.detail}
          onChange={(e) => setForm({ ...form, detail: e.target.value })}
        />
        <Box>
          <Button
            variant="contained"
            onClick={add}
            disabled={saving || (form.type === 'OTHER' && form.detail.trim() === '')}
          >
            {saving ? 'Saving…' : 'Add condition'}
          </Button>
        </Box>
      </Stack>

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default CaseConditionsPanel;
